import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { Button } from "@mui/material";
import { useGridSelection } from "hooks/omnisearch-v2";
import { useState } from "react";

import type { Row } from "./Manifests";

import { DiscrepancyReviewDialog } from "./DiscrepancyReviewDialog";
import { selectedManifestIds } from "./manifestsV3Selection";

/**
 * V2's bulk "Review Discrepancies" action on the merged list.
 *
 * The ids handed to the dialog are the selection reduced to manifest rows and
 * mapped to bare entity ids — never the grid's composite `<kind>:<entity id>`,
 * and never a bill of lading. The grid also refuses to select a bill-of-lading
 * row in the first place (`isRowSelectable` on the merged grid); this reduction
 * is what holds even if a row is selected some other way, and it is what the
 * count on the button describes.
 *
 * Reads the selection through `useGridSelection` rather than a controlled
 * selection model, so selecting rows re-renders this button alone instead of
 * the whole page. The rows are read during render (not memoized): the hook
 * re-renders this component on every selection change, which is exactly when
 * this list changes.
 */
export function BulkReviewDiscrepanciesButton() {
  const { getSelectedRows } = useGridSelection<Row>();
  const [reviewIds, setReviewIds] = useState<string[] | null>(null);

  const manifestIds = selectedManifestIds(getSelectedRows());

  return (
    <>
      {manifestIds.length > 0 && (
        <Button
          variant="outlined"
          color="warning"
          startIcon={<CompareArrowsIcon />}
          onClick={() => setReviewIds(manifestIds)}
        >
          Review Discrepancies ({manifestIds.length})
        </Button>
      )}
      {reviewIds && (
        <DiscrepancyReviewDialog
          processedManifestIds={reviewIds}
          onClose={() => setReviewIds(null)}
        />
      )}
    </>
  );
}
